import { PRODUCTS, isKorpoRegisterMode, isTransferRegisterMode, ProductType } from 'cart-model';
import { EventType } from 'common-event';
import { useTransRedirect } from 'common-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  Button,
  Title,
  InlineButton,
  ROI,
  createGenerateAutoTestId,
  useSelect,
} from 'ui-components';
import { StandardSortPanel } from 'ui-components/src/FundList/FundList';
import { useSortState } from 'ui-components/src/FundList/useSortState';
import { logChangeInvestmentAmount, logEvent, logQuotesChangeRate } from '../../model/events';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import BackButton from '../BackButton/BackButton';
import Screen from '../ProductWizard/Screen';
import { ProductWizardTransLink } from '../ProductWizardTransLink/ProductWizardTransLink';
import FundList from './FundList/FundList';
import PaymentCard from './PaymentCard';
import PotentialProfitInfoDialog from './PotentialProfitInfoDialog/PotentialProfitInfoDialog';

import useStyles from './SelectRecommendedFunds.style';

const SelectRecommendedFunds: React.FC = () => {
  const c = useStyles({});
  const [t] = useTranslation('select-recommended-funds');
  const [tP] = useTranslation('product-wizard');
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { sortConfig, requestSort, getSortedFunds } = useSortState();
  const [rateKey, handleSelectRateKey] = useSelect(ROI.M12);
  const handleSelectRateKeyWithLogEvent = (event: React.ChangeEvent<{ value: unknown }>) => {
    handleSelectRateKey(event);
    logQuotesChangeRate(event.target.value as string);
  };

  const initialAmount = useSelector((state: ProductWizardState) => state.uiState.initialAmount);
  const monthlyAmount = useSelector((state: ProductWizardState) => state.uiState.monthlyAmount);
  const productType = useSelector((state: ProductWizardState) => state.uiState.productType);

  const selectedRisks = useSelector((state: ProductWizardState) => state.uiState.risks);
  const isEntrepreneur = useSelector((state: ProductWizardState) => state.cartState.isEntrepreneur);
  const registerMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const funds = useSelector((state: ProductWizardState) => state.apiState.funds);
  const blockedFundIdns = useSelector((state: ProductWizardState) => state.apiState.blockedFundIdns);
  const testAutomationEnabled = useSelector((state: ProductWizardState) => state.config.testAutomationEnabled);
  const generateAutoTestId = createGenerateAutoTestId(testAutomationEnabled);
  const redirect = useTransRedirect();

  const unitCategoryCode = productType ? PRODUCTS[productType].unitCategory : undefined;

  const sortedFunds = (() => {
    if (!unitCategoryCode) return [];
    const filtered = funds
      .filter(fund => selectedRisks.map(value => value.id).includes(fund.risk.id))
      .filter(fund => fund.quotesForUnitCode.hasOwnProperty(unitCategoryCode))
      .filter(fund => {
        const afsCode = fund.afsCodeForUnitCode[unitCategoryCode];
        return !blockedFundIdns?.includes(afsCode);
      });
    return getSortedFunds(filtered, sortConfig, rateKey, unitCategoryCode) ?? filtered;
  })();

  const isCurrentCartKorpo = isKorpoRegisterMode(registerMode);
  const isCurrentCartTransfer = isTransferRegisterMode(registerMode);

  if (productType === undefined) {
    return <Redirect to="/"/>;
  }

  const handleChooseInvestmentAmount = (type: 'first' | 'monthly') => {
    logChangeInvestmentAmount(type);
    redirect(Screen.CHOOSE_INVESTMENT_AMOUNT.code);
  }

  return (
    <div className={c.root}>
      <BackButton />
      <div className={c.headerWrapper}>
        <Title
          title={tP(`title.${Screen.SELECT_RECOMMENDED_FUNDS.code}`)}
          description={productType !== ProductType.funds ? t(`title.${productType}`) : undefined}
          titleVariant='h1'
          titleComponent='h1'
          titleFontWeight="extraBold"
          titleClassName={c.headerTitle}
        />
        <PaymentCard
          initialAmount={initialAmount}
          monthlyAmount={monthlyAmount}
          onChooseInvestmentAmount={handleChooseInvestmentAmount}
        />
      </div>
      <div className={c.sortPanelWrapper}>
        {sortedFunds.length > 0 && (
          <StandardSortPanel
            className={c.sortPanel}
            handleSelectRateKey={handleSelectRateKeyWithLogEvent}
            sortConfig={sortConfig}
            requestSort={requestSort}
            rateKey={rateKey}
            hasUnitCategorySelect={false}
            withActionButton={true}
            isTestAutomationEnabled={testAutomationEnabled}
            generateAutoTestId={generateAutoTestId}
          />
        )}
        <InlineButton
          variant="underline"
          className={c.howWeCalculated}
          iconPlacement="left"
          onClick={() => setOpen(true)}
        >
          {t('how-we-calculated-profit')}
        </InlineButton>
      </div>
      <PotentialProfitInfoDialog open={open} onClose={() => setOpen(false)} />
      <FundList
        funds={sortedFunds}
        productType={productType}
        monthlyAmount={monthlyAmount}
        initialAmount={initialAmount}
        isEntrepreneur={isEntrepreneur}
        rateKey={rateKey as ROI}
      />
      <div className={c.selectRecommendedAllButton}>
        <ProductWizardTransLink path={Screen.SELECT_FUNDS.code}
                                currentScreenCode={Screen.SELECT_RECOMMENDED_FUNDS.code}
                                onClick={() => {
                                  if (isCurrentCartKorpo || isCurrentCartTransfer) {
                                    dispatch(productWizardActions.clearCart());
                                  }
                                  logEvent(EventType.preferenceClickSeeMore);
        }}>
          <Button buttonType="secondary">
            { t('see_all_funds', 'See all funds')}
          </Button>
        </ProductWizardTransLink>
      </div>
    </div>
  );
};

export default SelectRecommendedFunds;
